import {toJS} from 'mobx';
import {types, Instance, flow} from 'mobx-state-tree';
import {profileApi} from '@/api/account';
import moment from 'moment';
import {isNil} from 'lodash';

export const Subscription = types.model({
  id: types.maybeNull(types.number),
  isActive: types.maybeNull(types.boolean),
  seats: types.maybeNull(types.number),
  paymentAmount: types.maybeNull(types.string),
  monthlyCost: types.maybeNull(types.string),
  paymentFrequency: types.maybeNull(types.string),
  blEnabled: types.maybeNull(types.boolean),
  caEnabled: types.maybeNull(types.boolean),
  cxEnabled: types.maybeNull(types.boolean),
  gscEnabled: types.maybeNull(types.boolean),
  keEnabled: types.maybeNull(types.boolean),
  nextPaymentAt: types.maybeNull(types.string),
  lastPaidAt: types.maybeNull(types.string),
  activatedAt: types.maybeNull(types.string),
  activeUntil: types.maybeNull(types.string),
});

export type SubscriptionType = Instance<typeof Subscription>;

export const QuotaValues = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.number),
});

export const BudgetValues = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.number),
});

export const useDemoModel = types.model({
  total: types.maybeNull(types.boolean),
  consumed: types.maybeNull(types.number),
});

export const usMaxCompetitorsPerProjectModel = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.number),
});

export const useMaxNumberOfProjects = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.number),
});


export const historicalLimitModel = types.model({
  total: types.maybeNull(types.string),
  consumed: types.maybeNull(types.number),
});


export const BlQuota = types.model({
  budget: types.maybeNull(BudgetValues),
  useDemo: types.maybeNull(useDemoModel),
  maxCompetitorsPerProject: types.maybeNull(usMaxCompetitorsPerProjectModel),
  maxNumberOfProjects: types.maybeNull(useMaxNumberOfProjects),
  allowedBacklinkResearches: types.maybeNull(QuotaValues),
});

export const CheckerPerDay = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.number),
});
export const BulkQuota = types.model({
  daCheckerMaxLookupsPerDay: types.maybeNull(CheckerPerDay),
});

export const CaQuota = types.model({
  allowedFocusTerms: types.maybeNull(QuotaValues),
  allowedPages: types.maybeNull(QuotaValues),
  allowedSiteAuditorPages: types.maybeNull(QuotaValues),
  allowedOttoProjects: types.maybeNull(QuotaValues),
  allowedAiContentGeneration: types.maybeNull(QuotaValues),
  allowedAiPremiumContentGeneration: types.maybeNull(QuotaValues),
  allowedSiteAuditorProjects: types.maybeNull(QuotaValues),
  crawledPagesAllowedPerMonth: types.maybeNull(QuotaValues),
  allowedOnpageAudits: types.maybeNull(QuotaValues),
});

export const GscQuota = types.model({
  allowedSiteKeywords: types.maybeNull(QuotaValues),
  allowedPages: types.maybeNull(QuotaValues),
  allowedSitesToProcess: types.maybeNull(QuotaValues),
  historicalLimit: types.maybeNull(historicalLimitModel),
  projectDeactivationsAllowedPerMonth: types.maybeNull(QuotaValues),
  allowedActiveProjects: types.maybeNull(QuotaValues),
});

export const KeQuota = types.model({
  maxKeywordLookups: types.maybeNull(QuotaValues),
  maxKeywordLookupsPerWeek: types.maybeNull(QuotaValues),
  allowedCompetitorResearches: types.maybeNull(QuotaValues),
  allowedCompetitorResearchProjects: types.maybeNull(QuotaValues),
  allowedLocalSerpsHeatmapSearches: types.maybeNull(QuotaValues),
  allowedTrackedKeywords: types.maybeNull(QuotaValues),
  allowedTrackedProjects: types.maybeNull(QuotaValues),
  allowedContentPlans: types.maybeNull(QuotaValues),
});
export const QpQuota = types.model({
  allowedHaroReplies: types.maybeNull(QuotaValues),
});

export const QuotaInfo = types.model({
  bl: types.maybeNull(BlQuota),
  bulk: types.maybeNull(BulkQuota),
  ca: types.maybeNull(CaQuota),
  gsc: types.maybeNull(GscQuota),
  ke: types.maybeNull(KeQuota),
  qp: types.maybeNull(QpQuota),
  seats: types.maybeNull(types.number),
});

export const vendastaClientDetailsModel = types.model({
  isVendastaClient: types.maybeNull(types.boolean),
  dataUrl: types.maybeNull(types.string),
  dataAccountId: types.maybeNull(types.string),
  dataAppId: types.maybeNull(types.string),
});
export const teamModel = types.model({
  id: types.maybeNull(types.number),
  email: types.maybeNull(types.string),
});

export const PostOnboardingModel = types.model({
  keywords: types.maybeNull(types.array(types.string)),
  competitors: types.maybeNull(types.array(types.string)),
});

export type PostOnboardingType = Instance<typeof PostOnboardingModel>;

export const PostOnboardingSubmitModel = types.model({
  id: types.maybeNull(types.number),
  domainUrl: types.maybeNull(types.string),
  isCurrent: types.maybeNull(types.boolean),
  assignedUsers: types.maybeNull(types.array(types.string)),
  competitors: types.maybeNull(types.array(types.string)),
  keywords: types.maybeNull(types.array(types.string)),
});

export const Profile = types.model({
  paymentFailed: types.maybeNull(types.boolean),
  isVendastaClient: types.maybeNull(types.boolean),
  isWhitelabeled: types.maybeNull(types.boolean),
  logo: types.maybeNull(types.string),
  vendastaClientDetails: types.maybeNull(vendastaClientDetailsModel),
  cardLast4: types.maybeNull(types.string),
  id: types.maybeNull(types.number),
  customerId: types.maybeNull(types.number),
  firstName: types.maybeNull(types.string),
  lastName: types.maybeNull(types.string),
  emailAddress: types.string,
  seats: types.maybeNull(types.number),
  roles: types.maybeNull(types.array(types.string)),
  phoneNumber: types.maybeNull(types.string),
  trialExpiresAt: types.maybeNull(types.string),
  industry: types.maybeNull(types.string),
  isSubscriber: types.maybeNull(types.boolean),
  searchatlasApiKey: types.maybeNull(types.string),
  isTestAccount: types.maybeNull(types.boolean),
  isAutoTopupEnabled: types.maybeNull(types.boolean),
  useTestStripeKey: types.maybeNull(types.boolean),
  plan: types.maybeNull(types.number),
  planName: types.maybeNull(types.string),
  subscription: types.maybeNull(Subscription),
  team: types.maybeNull(types.array(teamModel)),
  contactName: types.maybeNull(types.string),
  hasCompletedRegistration: types.maybeNull(types.boolean),
  isVerified: types.maybeNull(types.boolean),
  quotaUtilization: types.maybeNull(QuotaInfo),
  creditsforBuying: types.maybeNull(types.number),
  firebaseKey: types.maybeNull(types.string),
  projectCount: types.maybeNull(types.number),
  showConfettiAnimation: types.boolean,
  productOverview: types.boolean,
  gscProductOverview: types.boolean,
  topUpAiQuotaLoading: types.boolean,
  emailAdress: types.maybeNull(types.string),
  name: types.maybeNull(types.string),
  registrationSource: types.maybeNull(types.string),
  postOnboarding: types.maybeNull(PostOnboardingModel),
  postOnboardingLoading: types.boolean,
  postOnboardingSubmitData: types.maybeNull(PostOnboardingSubmitModel),
  showPostOnboardingModal: types.boolean,
  showCreateProjectBanner: types.boolean,
  serviceTier: types.maybeNull(types.string),
  shouldDisableLivechat: types.maybeNull(types.boolean),
  currentCredits: types.maybeNull(types.number),
  loadHomePageLoading: types.maybeNull(types.boolean),
  getStatus: types.maybeNull(types.string),
  loadingStatus: types.maybeNull(types.boolean),
  isParentAccount: types.maybeNull(types.boolean),
  isDaFlowCompleted: types.maybeNull(types.boolean),
  shouldShowBillingPage: types.maybeNull(types.boolean),
  shouldShowPricingPage: types.maybeNull(types.boolean),
  shouldShowOrdersPage: types.maybeNull(types.boolean),
  shouldChildrenReceiveEmails: types.maybeNull(types.boolean),
  priceStarterPlan: types.maybeNull(types.number),
  priceGrowthPlan: types.maybeNull(types.number),
  priceProPlan: types.maybeNull(types.number),
  signInLogo: types.maybeNull(types.string),
  signUpLogo: types.maybeNull(types.string),
  companyName: types.maybeNull(types.string),
  apiRecallCount: types.maybeNull(types.number),
}).views(self => ({
  get fullName() {
    return (self.firstName && self.lastName) ? `${self.firstName} ${self.lastName}` : '';
  },
  get isWhitelabel() {
    const list = ['dashboard.linkgraph', 'dashboard.searchatlas', 'localhost'];
    const newList = [];
    if (typeof window !== 'undefined') {
      list.forEach(element => {
        if (location?.origin?.includes(element)) {
          newList.push(element);
        }
      });
    }
    if (typeof window !== 'undefined' && newList.length) {
      return false;
    }
    return self.isWhitelabeled;
    // return self.isWhitelabeled || typeof window !== 'undefined' && !(location?.origin?.includes('dashboard.linkgraph') || location?.origin?.includes('dashboard.searchatlas') || location?.origin?.includes('localhost'));
  },
  get getVendastaClientDetails() {
    return toJS(self.vendastaClientDetails);
  },
  get getRoles() {
    return toJS(self.roles);
  },
  get getMaxNumberOfSeats() {
    return self.seats;
  },
  get isLinkgraph() {
    return self.customerId === 439 || self.customerId === 15774 || self.customerId === 51914;
  },
  get isLinkgraphSpecificIds() {
    return self.customerId === 439 || self.customerId === 15774 || self.customerId === 51914 || self.customerId === 55443 || self.customerId === 51750 || self.customerId === 790;
  },

  get isKRQuotaLimitReached() {
    const totalData = self.quotaUtilization?.ke?.maxKeywordLookups?.total;
    let consumedData = self.quotaUtilization?.ke?.maxKeywordLookups?.consumed;
    if ([null, undefined, 0].includes(totalData)) {
      return true;
    }
    if ([null, undefined].includes(consumedData)) {
      consumedData = 0;
    }
    return consumedData >= totalData;
  },
  get isFocusTermsQuotaLimitReached() {
    const totalData = self.quotaUtilization?.ca?.allowedFocusTerms?.total;
    let consumedData = self.quotaUtilization?.ca?.allowedFocusTerms?.consumed;
    if ([null, undefined, 0].includes(totalData)) {
      return true;
    }
    if ([null, undefined].includes(consumedData)) {
      consumedData = 0;
    }
    return consumedData >= totalData;
  },

  get focusTermsQuotaLimit() {
    let totalData = self.quotaUtilization?.ca?.allowedFocusTerms?.total;
    let consumedData = self.quotaUtilization?.ca?.allowedFocusTerms?.consumed;
    if ([null, undefined, 0].includes(totalData)) {
      totalData = 0;
    }
    if ([null, undefined].includes(consumedData)) {
      consumedData = 0;
    }
    return {totalData, consumedData};
  },

  get isAllowedPagesQuotaLimitReached() {
    const totalData = self.quotaUtilization?.ca?.allowedPages?.total;
    let consumedData = self.quotaUtilization?.ca?.allowedPages?.consumed;
    if ([null, undefined, 0].includes(totalData)) {
      return true;
    }
    if ([null, undefined].includes(consumedData)) {
      consumedData = 0;
    }
    return consumedData >= totalData;
  },

  get isAllowedAiContentGenerationQuotaLimitReached() {
    const totalData = self.quotaUtilization?.ca?.allowedAiContentGeneration?.total;
    let consumedData = self.quotaUtilization?.ca?.allowedAiContentGeneration?.consumed;
    if ([null, undefined, 0].includes(totalData)) {
      return true;
    }
    if ([null, undefined].includes(consumedData)) {
      consumedData = 0;
    }
    return consumedData >= totalData;
  },

  get isAllowedAiPremiumContentGenerationQuotaLimitReached() {
    const totalData = self.quotaUtilization?.ca?.allowedAiPremiumContentGeneration?.total;
    let consumedData = self.quotaUtilization?.ca?.allowedAiPremiumContentGeneration?.consumed;
    if ([null, undefined, 0].includes(totalData)) {
      return true;
    }
    if ([null, undefined].includes(consumedData)) {
      consumedData = 0;
    }
    return consumedData >= totalData;
  },

  get isAllowedSiteAuditorPagesQuotaLimitReached() {
    const totalData = self.quotaUtilization?.ca?.allowedSiteAuditorPages?.total;
    let consumedData = self.quotaUtilization?.ca?.allowedSiteAuditorPages?.consumed;
    if ([null, undefined, 0].includes(totalData)) {
      return true;
    }
    if ([null, undefined].includes(consumedData)) {
      consumedData = 0;
    }
    return consumedData >= totalData;
  },

  get GSCAllowActiveProjectsTotal() {
    return self.quotaUtilization?.gsc?.allowedActiveProjects?.total;
  },

  get GSCAllowActiveProjectsConsumed() {
    return self.quotaUtilization?.gsc?.allowedActiveProjects?.consumed;
  },

  get GSCAllowPagesTotal() {
    return self.quotaUtilization?.gsc?.allowedPages?.total;
  },

  get GSCAllowPagesConsumed() {
    return self.quotaUtilization?.gsc?.allowedPages?.total;
  },

  get GSCAllowSiteKeywordsTotal() {
    return self.quotaUtilization?.gsc?.allowedSiteKeywords?.total;
  },

  get GSCAllowSiteKeywordsConsumed() {
    return self.quotaUtilization?.gsc?.allowedSiteKeywords?.consumed;
  },

  get maxProjectsPerQuota() {
    return self.quotaUtilization?.bl?.maxNumberOfProjects?.total;
  },

  get consumedProjects() {
    return self.quotaUtilization?.bl?.maxNumberOfProjects?.consumed;
  },

  get getMaxCompetitorsPerProject() {
    return self.quotaUtilization?.bl?.maxCompetitorsPerProject?.total;
  },

  get getConsumedCompetitorsPerProject() {
    return self.quotaUtilization?.bl?.maxCompetitorsPerProject?.consumed;
  },

})).actions(self => {
  const setFieldValue = (attr: string, value: string) => {
    self[attr] = value;
  };

  const setLogoInStore = value => {
    self.logo = value;
  };

  const setSignInLogoInStore = value => {
    self.signInLogo = value;
  };

  const setSignUpLogoInStore = value => {
    self.signUpLogo = value;
  };

  const togglePlan = (checked: boolean) => {
    if (checked) {
      self.plan = null;
      self.isSubscriber = false;
    } else {
      self.plan = 1;
      self.isSubscriber = true;
    }
  };

  const updatePlan = ( value: number) => {
    self.plan = value;
    self.isSubscriber = value != 0;
  };


  const setIsDaFlowCompleted = ( value: boolean) => {
    self.isDaFlowCompleted = value;
  };

  const updateEmail = ( value: string) => {
    self.emailAdress = value;
  };

  const updateName = ( value: string) => {
    self.name = value;
  };

  const setProductOverview = (value: boolean) => {
    self.productOverview = value;
  };

  const setGscProductOverview = (value: boolean) => {
    self.gscProductOverview = value;
  };

  // used for 429 status code to match consumed with total AI quota, to avoid calling /user/ endpoint
  const matchAiConsumedTotalQuota = () => {
    if (self.quotaUtilization?.ca?.allowedAiContentGeneration?.total) {
      const totalQuota = self.quotaUtilization?.ca?.allowedAiContentGeneration?.total;
      self.quotaUtilization.ca.allowedAiContentGeneration.consumed = totalQuota;
    }
  };
  const setLoadStatusLoading = value=>{
    self.loadingStatus = value;
  };
  const setLoadHomePage = value => {
    self.loadHomePageLoading = value;
  };
  const setShowPostOnboardingModal = (value: boolean) => self.showPostOnboardingModal = value;
  const setShowCreateProjectBanner = (value: boolean) => self.showCreateProjectBanner = value;
  const setCreditsforBuying= (value: number) => {
    self.creditsforBuying = value;
  };

  return {
    setCreditsforBuying,
    updateEmail,
    updateName,
    setLogoInStore,
    setLoadStatusLoading,
    setFieldValue,
    togglePlan,
    setLoadHomePage,
    updatePlan,
    setProductOverview,
    setGscProductOverview,
    matchAiConsumedTotalQuota,
    setShowPostOnboardingModal,
    setShowCreateProjectBanner,
    setIsDaFlowCompleted,
    setSignInLogoInStore,
    setSignUpLogoInStore,
  };
});

const addonResourceQuotaDg = types.model({
  maxMonthlyActions: types.maybeNull(types.number),
  drProjects: types.maybeNull(types.number),
  googlemapsProjects: types.maybeNull(types.number),
  googlesearchProjects: types.maybeNull(types.number),
});
const addonResourceQuota = types.model({
  dg: types.maybeNull(addonResourceQuotaDg),
});
const maxMonthlyActions = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.union(types.float, types.number)),
});
const drProjects = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.union(types.float, types.number)),
});
const googlemapsProjects = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.union(types.float, types.number)),
});
const googlesearchProjects = types.model({
  total: types.maybeNull(types.number),
  consumed: types.maybeNull(types.union(types.float, types.number)),
});
const addonResourceQuotaUsageDg = types.model({
  maxMonthlyActions: types.maybeNull(maxMonthlyActions),
  drProjects: types.maybeNull(drProjects),
  googlemapsProjects: types.maybeNull(googlemapsProjects),
  googlesearchProjects: types.maybeNull(googlesearchProjects),
});
const addonResourceQuotaUsage = types.model({
  dg: types.maybeNull(addonResourceQuotaUsageDg),
});
const subscription = types.model({
  isActive: types.maybeNull(types.boolean),
  nextPaymentAt: types.maybeNull(types.string),
});

const userQuotaModel = types.model({
  customerId: types.maybeNull(types.number),
  addonResourceQuota: types.maybeNull(addonResourceQuota),
  addonResourceQuotaUsage: types.maybeNull(addonResourceQuotaUsage),
  addonId: types.maybeNull(types.number),
  addonName: types.maybeNull(types.string),
  subscription: types.maybeNull(subscription),
});

export type ProfileType = Instance<typeof Profile>;

export const Customer = types.model({
  profile: types.maybeNull(Profile),
  loading: types.optional(types.boolean, false),
  isInitialExtendedDetailsFetched: types.boolean,
  userQuota: types.maybeNull(userQuotaModel),
  loadingQuote: types.optional(types.boolean, false),
}).views(self => ({
  get remainingExpiryDays() {
    const now = moment();
    const expire = moment(self.profile.trialExpiresAt);
    return expire.diff(now, 'days');
  },

  get isFreeTrialExpire() {
    if (!self.profile.trialExpiresAt || self.profile.isSubscriber) {
      return false;
    }
    const now = moment();
    const expire = moment(self.profile.trialExpiresAt);
    return moment(now).isAfter(expire);
  },
})).actions(self => {
  const setFromResponse = (profile: ProfileType) => {
    // self.profile.cardLast4=profile?.cardLast4;
    self.profile.id = profile?.id;
    self.profile.customerId = profile?.customerId;
    self.profile.firstName = profile?.firstName;
    self.profile.lastName = profile?.lastName;
    self.profile.emailAddress = profile?.emailAddress || ''; '';
    self.profile.phoneNumber = profile?.phoneNumber;
    self.profile.roles = profile?.roles;
    self.profile.logo = profile?.logo;
    self.profile.trialExpiresAt = profile?.trialExpiresAt;
    self.profile.industry = profile?.industry;
    self.profile.isSubscriber = profile?.isSubscriber;
    self.profile.isTestAccount = profile?.isTestAccount;
    self.profile.isParentAccount = profile?.isParentAccount;
    self.profile.companyName = profile?.companyName;
    // isNil check will be removed after this key is added to /user/ endpoint
    // we use it now to prevent setting this key in mobx as null, while waiting for BE
    if (!isNil(profile?.isVendastaClient)) {
      self.profile.isVendastaClient = profile?.isVendastaClient;
    }
    self.profile.useTestStripeKey = profile?.useTestStripeKey;
    self.profile.plan = profile?.plan;
    self.profile.subscription = profile?.subscription;
    self.profile.contactName = profile?.firstName ? `${profile?.firstName} ${profile?.lastName}` : '';
    self.profile.isVerified = profile?.isVerified;
    self.profile.hasCompletedRegistration = profile?.hasCompletedRegistration;
    self.profile.firebaseKey = profile?.firebaseKey;
    self.profile.projectCount = profile?.projectCount;
    self.profile.customerId = profile?.customerId;
    self.profile.shouldShowBillingPage = profile?.shouldShowBillingPage;
    self.profile.shouldShowPricingPage = profile?.shouldShowPricingPage;
    self.profile.shouldShowOrdersPage = profile?.shouldShowOrdersPage;
    self.profile.shouldChildrenReceiveEmails = profile?.shouldChildrenReceiveEmails;
    self.profile.priceStarterPlan = profile?.priceStarterPlan;
    self.profile.priceGrowthPlan = profile?.priceGrowthPlan;
    self.profile.priceProPlan = profile?.priceProPlan;
    self.profile.isWhitelabeled = profile?.isWhitelabel;
    self.profile.shouldDisableLivechat = profile?.shouldDisableLivechat;
    self.profile.isParentAccount = profile?.isParentAccount;
    if (profile?.hasCompletedRegistration) {
      localStorage.setItem('registerDate', Date.now()?.toString());
    }
  };

  const loadProfile = flow(function* () {
    self.loading = true;
    try {
      const data = yield profileApi.getProfile();
      if (data?.emailAddress) {
        self.isInitialExtendedDetailsFetched = true;
        setFromResponse(data);
      }
      return data;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      self.loading = false;
    }
  });
  const loadQuota = flow(function* (userId) {
    self.loadingQuote = true;
    try {
      const response = yield profileApi.getQuota(userId);
      if (response?.data) {
        self.userQuota = response.data;
      }
      return response.data;
    } catch (error) {
      return Promise.reject(error);
    } finally {
      self.loadingQuote = false;
    }
  });

  return {
    loadProfile,
    loadQuota,
  };
});

export function initCustomer() {
  return Customer.create({
    profile: Profile.create({
      emailAdress: '',
      creditsforBuying: 0,
      name: '',
      firstName: null,
      isVendastaClient: false,
      isWhitelabeled: true,
      logo: '',
      vendastaClientDetails: {
        dataUrl: '',
        dataAccountId: '',
        dataAppId: '',
      },
      isDaFlowCompleted: false,
      productOverview: true,
      gscProductOverview: false,
      topUpAiQuotaLoading: false,
      lastName: null,
      emailAddress: '',
      phoneNumber: '',
      industry: '',
      isSubscriber: false,
      searchatlasApiKey: '',
      registrationSource: 'domain_analyzer',
      isAutoTopupEnabled: false,
      isTestAccount: false,
      useTestStripeKey: false,
      plan: 0,
      subscription: {
        activatedAt: '',
      },
      team: [],
      contactName: '',
      cardLast4: '',
      hasCompletedRegistration: false,
      quotaUtilization: {
        bl: null,
        ca: null,
        gsc: null,
        ke: null,
        seats: null,
      },
      showConfettiAnimation: false,
      postOnboarding: {
        competitors: null,
        keywords: null,
      },
      postOnboardingLoading: false,
      postOnboardingSubmitData: null,
      showPostOnboardingModal: false,
      showCreateProjectBanner: true,
      loadHomePageLoading: false,
      getStatus: '',
      loadingStatus: false,
    }),
    isInitialExtendedDetailsFetched: false,
  });
}
